import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { benefitsData } from '../mock';
import { Shield, Clock, Sparkles, CheckCircle, Leaf, DollarSign } from 'lucide-react';

const iconMap = {
  '01': Shield,
  '02': Clock,
  '03': Sparkles,
  '04': CheckCircle,
  '05': Leaf,
  '06': DollarSign,
};

export const Benefits = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-32 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(20 184 166) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4">
            <span className="text-8xl md:text-9xl font-bold text-teal-50">03</span>
            <div>
              <span className="block text-teal-600 font-semibold text-sm uppercase tracking-wider">Chapter Three</span>
              <span className="block text-2xl font-semibold text-gray-900">Why ADL</span>
            </div>
          </div>
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            More Than Just a Clean
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We've spent 20 years perfecting our craft. Here's what sets ADL apart from the rest.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefitsData.map((benefit, index) => {
            const Icon = iconMap[benefit.number];
            return (
              <motion.div
                key={benefit.number}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="relative bg-gradient-to-br from-white to-teal-50/30 rounded-3xl p-8 shadow-lg shadow-gray-200/50 border border-gray-100 h-full hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-300">
                  {/* Number Badge */}
                  <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <span className="text-2xl font-bold text-white">{benefit.number}</span>
                  </div>

                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : { scale: 0 }}
                    transition={{ delay: index * 0.1 + 0.3, type: 'spring', stiffness: 200 }}
                    className="mb-6 mt-8"
                  >
                    <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                      <Icon className="w-7 h-7 text-teal-600" />
                    </div>
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-teal-700 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
